import { Capacitor } from '@capacitor/core'

/**
 * Darf dieser Build einen Kaufweg zeigen?
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM ES DIESE EINE STELLE GIBT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * StaySuite geht als **kostenlose Begleit-App** in den App Store — Guideline
 * 3.1.3(f) „Free Stand-alone Apps": eine App, die einem bezahlten Web-Werkzeug
 * zur Seite steht, darf ohne In-App-Kauf auskommen. Die Bedingung dafür ist
 * strenger, als sie klingt: verlangt wird nicht bloß „kein Kaufknopf", sondern
 * **„no calls to action for purchase outside of the app"**. Ein Preis, den die
 * App nur nennt, ist schon einer. Eine Zeile „Danach so und so viel pro Monat"
 * reicht — und ein Formular, das zum Abschluss außerhalb der App führt, ebenso.
 *
 * Deshalb steht die Entscheidung hier und nicht sieben Mal verstreut. Es gab
 * sieben Stellen, die in den Abo-Weg führten — zwei Routen, das Testphasen-Band,
 * ein Hinweis der KI-Nutzung, die Karte daneben, das Ende der Ersteinrichtung
 * und die Registrierung. Wer eine davon vergisst, merkt es nicht beim Bauen,
 * sondern Tage später an einer Ablehnung im Review.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WAS DAS NICHT BETRIFFT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **Die Zahlung des Gastes an den Vermieter bleibt unangetastet.** Eine
 * Übernachtung ist eine Leistung in der echten Welt, und für die verlangt
 * Guideline 3.1.3(e) ausdrücklich das Gegenteil: „you **must** use purchase
 * methods other than in-app purchase". Stripe Connect, `CollectPayment` und der
 * Bezahllink auf der Rechnung sind also nicht bloß erlaubt, sondern der
 * vorgeschriebene Weg. Sie dürfen hier nicht mit abgeräumt werden.
 *
 * Betroffen ist allein das **Abo des Vermieters** — ein digitales Abonnement,
 * das Funktionen freischaltet, und damit der Fall, auf den 3.1.1 zielt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  GOOGLE PLAY — dieselbe Wirkung, eine ANDERE Begründung
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `Capacitor.isNativePlatform()` ist wahr für iOS **und** Android. Die Sperre
 * trägt also beide Läden. **Die Begründung oben trägt nur einen davon.**
 *
 * Google Play kennt **keine** Entsprechung zu Apples 3.1.3(f) „Free Stand-alone
 * Apps" — keine Begleit-App-Ausnahme und keine B2B-Ausnahme. Im Gegenteil, aus
 * der Payments Policy (abgerufen 03.08.2026): Play Billing ist erforderlich für
 * „cloud software and services (such as data storage services, **business
 * productivity software**…)", und Apps „may not lead users to a payment method
 * other than Google Play's billing system" — ausdrücklich auch nicht über
 * „in-app promotions, **webviews, buttons, links, messaging**…or calls to
 * action".
 *
 * Praktisch geht es trotzdem auf, weil diese Datei **gar keinen** Kaufweg zeigt.
 * Aber wer die Begründung oben liest und daraus schließt, für Android gelte
 * dieselbe Ausnahme, irrt sich — dort gibt es keine, auf die man sich berufen
 * könnte. Wer hier je lockert, muss für Android eigens prüfen.
 *
 * Nachgetragen am 03.08.2026, nachdem eine Nachbarsitzung darauf hinwies, dass
 * hier nur Apple steht. Zitate mit Abrufdatum, weil Store-Regeln sich bewegen —
 * im EU-Raum zusätzlich durch den DMA. Eine Notiz ohne Quelle ist in drei
 * Monaten ein Verdacht, keine Auskunft.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WIE MAN DAS PRÜFT — und wie NICHT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **Nicht durch eine Zeichenkettensuche im gebauten Paket.** Web und Hülle
 * teilen dasselbe Bundle (`webDir: 'dist'`); die Entscheidung fällt zur
 * Laufzeit. `/billing/setup` und die Preise stehen deshalb **zwangsläufig** im
 * Android- und iOS-Paket, obwohl sie dort nie erreichbar sind. Eine Suche
 * darüber lässt einen korrekten Build durchfallen — ein garantierter Fehlalarm,
 * der beim zweiten Mal ausgebaut wird und dann nichts mehr prüft.
 *
 * Prüfbar ist nur das **Verhalten**: `kaufweg.test.ts` hält fest, dass jede
 * Stelle, die in den Abo-Weg führt, diesen Schalter abfragt. Dazu gehört ein
 * Blick in die laufende Hülle mit einem **angemeldeten** Konto — in der Demo
 * erscheint der Kaufweg ohnehin nicht, dort wäre nichts zu sehen. Genau so ist
 * am 03.08.2026 eine Preisangabe in `AiUsageCard.tsx` gefunden worden, die der
 * Quelltextfund nicht hergab: Dieselbe Datei bewachte einen Satz und übersah
 * einen zweiten mit demselben Inhalt.
 *
 * Die Rechnung hinter der Entscheidung, die verworfenen Wege und die Zahlen
 * stehen in `docs/specs/006-abrechnung-ueber-apple.md`.
 */
export type Kaufweg = 'stripe' | 'apple' | 'google' | 'keiner'

/*
 * Die zwei Schalter des Laden-Kaufwegs (Vorgang 009, AK46/G8) — und warum es
 * ZWEI sind:
 *
 *   1. Die Build-Konstante hier sagt, ob der Kaufweg dieser Plattform
 *      NACHGEWIESEN und live ist. Sie fällt einzeln je Laden, nie zusammen.
 *   2. Der Serverschalter `kaufweg_offen` (RPC, Vorgang 009 Schnitt 8) ist der
 *      Rückzugsweg OHNE Store-Release: Der Kaufknopf erscheint nur, wenn
 *      kaufweg() einen Laden nennt UND der Server `true` meldet (X9/A8.6).
 *      Ein Rückzug ist damit eine Einstellung, kein Review-Durchlauf.
 *
 * Solange kaufweg() für eine Plattform 'keiner' sagt, erscheint dort KEIN
 * Preis, kein Verweis, kein Hinweistext mit Betrag — die Begründung samt
 * Zitaten und Abrufdatum steht oben in dieser Datei.
 */

/** Ist der Kaufweg dieser Plattform nachgewiesen und live? Fällt EINZELN. */
const ANDROID_LADEN_LIVE = false // fällt mit Schnitt 7 — zurückgestellt (Renés Entscheid 19.08.2026)
const IOS_LADEN_LIVE = false     // fällt mit A8.6/A8.7 (AX-Proben + V10-Genehmigung), nie zusammen mit Android

/** Welcher Kaufweg gilt in diesem Build? Die eine Stelle, an der das entschieden wird. */
export function kaufweg(): Kaufweg {
  if (!Capacitor.isNativePlatform()) return 'stripe'
  if (Capacitor.getPlatform() === 'android') return ANDROID_LADEN_LIVE ? 'google' : 'keiner'
  if (Capacitor.getPlatform() === 'ios') return IOS_LADEN_LIVE ? 'apple' : 'keiner'
  return 'keiner'
}

/** Bedeutung und Antworten unverändert: zeigt dieser Build den STRIPE-Weg? */
export function kaufwegSichtbar(): boolean {
  return kaufweg() === 'stripe'
}

export type PayChannel = 'stripe' | 'apple' | 'google' | null

/**
 * Erscheint der LADEN-Kaufknopf? Die vierteilige Entscheidung als REINE
 * Funktion (Spec AK47): Es gibt keine DOM-Testumgebung und es wird keine
 * geben — ohne diese Funktion bliebe die Kombinatorik dauerhaft Handarbeit.
 * `LadenAboView` ruft sie; sie selbst liest nichts und lädt nichts.
 *
 * Die vier Achsen, jede mit ihrem Grund:
 *
 *   1. `weg` — nur ein LADEN verkauft hier ('apple' | 'google'). Der Browser
 *      kauft über `/billing/setup`, und 'keiner' zeigt nichts (3.1.1 bzw.
 *      Googles Payments Policy).
 *   2. `kaufwegOffen` — der Serverschalter, der Rückrollpfad der Hülle (X9):
 *      `false` nimmt den Knopf beim nächsten App-Start weg, ohne Release.
 *      Eine Ansicht, die nur `kaufweg()` fragt und diesen Schalter ignoriert,
 *      zeigt einen Kaufknopf, den niemand mehr abschalten kann.
 *   3. `rolle` — nur `owner` kauft (AK16); die Sperre greift VOR dem
 *      Kaufblatt, nicht nach dem Bezahlen.
 *   4. `payChannel` — zahlt der Betrieb bereits über einen ANDEREN Kanal,
 *      gibt es keinen Kaufknopf (AK10/AU3). Der EIGENE Laden ist dagegen
 *      kein Hindernis: ein laufendes Apple-Abo wechselt sein Paket über
 *      einen weiteren Kauf in derselben Abo-Gruppe — Apple sagt dann selbst,
 *      was schon abonniert ist.
 */
export function kaufknopfSichtbar(
  weg: Kaufweg,
  kaufwegOffen: boolean,
  rolle: 'owner' | 'manager' | 'staff' | 'viewer' | null,
  payChannel: PayChannel,
): boolean {
  if (weg !== 'apple' && weg !== 'google') return false
  if (!kaufwegOffen) return false
  if (rolle !== 'owner') return false
  if (payChannel !== null && payChannel !== weg) return false
  return true
}
